/**
 * Converts the first character of the given string to upper case (only if it is a letter)
 *
 * @example
 * capitalize('abc') === 'Abc'
 * capitalize('Abc') === 'Abc'
 * capitalize('123') === '123'
 * capitalize('')    === ''
 */
export function capitalize<T extends string>(str: T): Capitalize<T> {
  return (
    str && (str.replace(/^[a-zA-Z]/, c => c.toUpperCase()) as Capitalize<T>)
  )
}

/**
 * Converts the first character of the given string to lower case (only if it is a letter)
 *
 * @example
 * uncapitalize('abc') === 'abc'
 * uncapitalize('Abc') === 'abc'
 * uncapitalize('123') === '123'
 * uncapitalize('')    === ''
 */
export function uncapitalize<T extends string>(str: T): Uncapitalize<T> {
  return (
    str && (str.replace(/^[a-zA-Z]/, c => c.toLowerCase()) as Uncapitalize<T>)
  )
}

/**
 * Represents a [letter case](https://en.wikipedia.org/wiki/Letter_case) that
 * can be used for a given string
 */
export enum StringCase {
  Camel = 'camelCase',
  Kebab = 'kebab-case',
  Snake = 'snake_case',
  Title = 'TitleCase',
  Upper = 'UPPER_CASE',
}
export namespace StringCase {
  export type Apply<TCase extends StringCase, S extends string> = {
    [StringCase.Camel]: string // TODO:
    [StringCase.Kebab]: KebabCase<S>
    [StringCase.Snake]: SnakeCase<S>
    [StringCase.Title]: string // TODO:
    [StringCase.Upper]: Uppercase<SnakeCase<S>>
  }[TCase]
}

/**
 * Determines the {@link StringCase} of the given string, if one applies
 */
export function getCase(
  str: string,
  { ignoreLeadingUnderscore = false }: getCase.Options = {},
): StringCase | undefined {
  if (ignoreLeadingUnderscore) {
    str = str.replace(/^_+/, '')
  }
  if (/^[^A-Z_-]+(-[^A-Z_-]+)*$/.test(str)) {
    return StringCase.Kebab
  } else if (/^[^A-Z_-]+(_[^A-Z_-]+)*$/.test(str)) {
    return StringCase.Snake
  } else if (/^[^a-z_-]+(_[^a-z_-]+)*$/.test(str)) {
    return StringCase.Upper
  } else if (/^[^a-zA-Z_-]*[a-z][^_-]*$/.test(str)) {
    return StringCase.Camel
  } else if (/^[^a-zA-Z_-]*[A-Z][^_-]*$/.test(str)) {
    return StringCase.Title
  }
  return undefined
}
export namespace getCase {
  export interface Options {
    /** @default false */
    ignoreLeadingUnderscore?: boolean
  }
}

/**
 * Splits the words in the given string based on its {@link StringCase}
 */
export function getWords(
  str: string,
  _case: StringCase | undefined = getCase(str),
): string[] {
  if (!_case) {
    // NOTE: JSON.stringify escapes quotes
    throw new Error(`Cannot infer current case of: ${JSON.stringify(str)}`)
  }
  if (_case === StringCase.Camel) {
    str = capitalize(str)
    _case = StringCase.Title
  } else if (_case === StringCase.Upper) {
    str = str.toLowerCase()
    _case = StringCase.Snake
  }
  let words: string[]
  switch (_case) {
    case StringCase.Title:
      words = str.match(/([A-Z]|^)[^A-Z]*/g) ?? []
      break
    case StringCase.Snake:
      words = str.split('_')
      break
    case StringCase.Kebab:
      words = str.split('-')
      break
    default:
      _assertsIsNever(_case)
  }
  return words
}

/**
 * Can be used to convert a given string to a given {@link StringCase}
 */
export function transformCase<S extends string, TCase extends StringCase>(
  str: S,
  desiredCase: TCase,
): StringCase.Apply<TCase, S>
export function transformCase(str: string, desiredCase: StringCase): string {
  if (str === '') {
    return ''
  }
  const prefix = /^[_-]*/.exec(str)?.[0] ?? ''
  const suffix = /[_-]*$/.exec(str)?.[0] ?? ''
  str = str.substring(prefix.length, str.length - suffix.length)
  const getReassembledStr = () => `${prefix}${str}${suffix}`
  const currentCase = getCase(str)
  if (currentCase === desiredCase) {
    return getReassembledStr()
  }
  const words = getWords(str, currentCase)
  switch (desiredCase) {
    case StringCase.Camel:
      str = uncapitalize(words.map(word => capitalize(word)).join(''))
      break
    case StringCase.Kebab:
      str = words.map(word => word.toLowerCase()).join('-')
      break
    case StringCase.Snake:
      str = words.map(word => word.toLowerCase()).join('_')
      break
    case StringCase.Title:
      str = words.map(word => capitalize(word)).join('')
      break
    case StringCase.Upper:
      str = words.map(word => word.toUpperCase()).join('_')
      break
    default:
      _assertsIsNever(desiredCase)
  }
  return getReassembledStr()
}

// NOTE: cannot use TypeChecker as that would cause circular dependency
function _assertsIsNever(_case: never): never {
  throw new Error(`Unsupported case: ${_case as string}`)
}
